import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getTrackingSettings } from '../../utils/api';
import {
    isPrivateApplicationPath,
    saveCookiePreferences,
    useCookiePreferences,
} from './cookieConsent';
import {
    DEFAULT_COOKIE_PREFERENCES_COPY,
    normalizeCookiePreferencesCopy,
} from './cookiePreferencesCopy';

const buttonBase = 'min-h-[44px] rounded-sm px-5 py-2.5 text-sm font-bold transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ekc-light-green focus-visible:ring-offset-2 focus-visible:ring-offset-custom-black';

const CookiePreferences = () => {
    const { pathname } = useLocation();
    const preferences = useCookiePreferences();
    const [isOpen, setIsOpen] = useState(() => preferences === null);
    const [isManaging, setIsManaging] = useState(false);
    const [analyticsEnabled, setAnalyticsEnabled] = useState(() => preferences?.analytics ?? false);
    const [copy, setCopy] = useState(DEFAULT_COOKIE_PREFERENCES_COPY);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const isPrivatePath = isPrivateApplicationPath(pathname);

    useEffect(() => {
        if (isPrivatePath) return undefined;
        let active = true;
        getTrackingSettings()
            .then((response) => {
                if (active) setCopy(normalizeCookiePreferencesCopy(response?.data?.cookieBanner));
            })
            .catch(() => {
                // Keep the familiar default wording when CMS settings are unavailable.
        });
        return () => { active = false; };
    }, [isPrivatePath]);

    useEffect(() => {
        if (preferences === null) {
            setIsOpen(true);
            setAnalyticsEnabled(false);
            return;
        }

        setAnalyticsEnabled(preferences.analytics);
    }, [preferences]);

    useEffect(() => {
        if (isOpen && isManaging) headingRef.current?.focus();
    }, [isManaging, isOpen]);

    if (isPrivatePath) return null;

    const chooseAnalytics = (analytics: boolean) => {
        saveCookiePreferences(analytics);
        setAnalyticsEnabled(analytics);
        setIsManaging(false);
        setIsOpen(false);
    };

    return (
        <div className="fixed inset-x-0 bottom-0 z-[100] pointer-events-none font-jakarta">
            <AnimatePresence initial={false}>
                {isOpen ? (
                    <motion.section
                        key="cookie-preferences-panel"
                        aria-labelledby="cookie-preferences-title"
                        className="pointer-events-auto mx-auto mb-3 max-h-[calc(100vh-1.5rem)] w-[calc(100%-1.5rem)] max-w-5xl overflow-y-auto overscroll-contain border border-white/15 bg-custom-black text-white shadow-[0_24px_70px_rgba(0,0,0,0.38)] sm:mb-5 sm:max-h-[calc(100vh-2.5rem)]"
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 18 }}
                        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="h-1 w-full bg-ekc-light-green" aria-hidden="true" />
                        <div className="p-5 sm:p-6 lg:px-8">
                            <AnimatePresence mode="wait" initial={false}>
                                {isManaging ? (
                                    <motion.div
                                        key="manage-preferences"
                                        initial={{ opacity: 0, x: 12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -12 }}
                                        transition={{ duration: 0.18 }}
                                        onAnimationComplete={() => headingRef.current?.focus()}
                                    >
                                        <div className="flex items-start justify-between gap-5 border-b border-white/15 pb-4">
                                            <div>
                                                <p className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-ekc-light-green">{copy.manageEyebrow}</p>
                                                <h2
                                                    id="cookie-preferences-title"
                                                    ref={headingRef}
                                                    tabIndex={-1}
                                                    className="text-xl font-bold outline-none sm:text-2xl"
                                                >
                                                    {copy.manageTitle}
                                                </h2>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setIsManaging(false)}
                                                className="min-h-[44px] px-2 text-sm font-semibold text-white/75 transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ekc-light-green"
                                            >
                                                {copy.backLabel}
                                            </button>
                                        </div>

                                        <div className="divide-y divide-white/15">
                                            <div className="flex items-center justify-between gap-5 py-4">
                                                <div>
                                                    <h3 className="font-bold">{copy.essentialTitle}</h3>
                                                    <p className="mt-1 max-w-2xl text-sm leading-6 text-white/70">{copy.essentialDescription}</p>
                                                </div>
                                                <span className="shrink-0 text-sm font-bold text-ekc-light-green">{copy.essentialStatusLabel}</span>
                                            </div>
                                            <div className="flex items-center justify-between gap-5 py-4">
                                                <div>
                                                    <h3 className="font-bold">{copy.analyticsTitle}</h3>
                                                    <p className="mt-1 max-w-2xl text-sm leading-6 text-white/70">{copy.analyticsDescription}</p>
                                                </div>
                                                <div
                                                    role="group"
                                                    aria-label="Analytics cookie preference"
                                                    className="flex shrink-0 rounded-sm border border-white/35 bg-white/10 p-1"
                                                >
                                                    <button
                                                        type="button"
                                                        aria-pressed={!analyticsEnabled}
                                                        aria-label="Disable analytics"
                                                        onClick={() => setAnalyticsEnabled(false)}
                                                        className={`min-h-[36px] min-w-[48px] rounded-sm px-3 text-sm font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ekc-light-green ${!analyticsEnabled ? 'bg-white text-custom-black' : 'text-white/70 hover:text-white'}`}
                                                    >
                                                        {copy.offLabel}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        aria-pressed={analyticsEnabled}
                                                        aria-label="Enable analytics"
                                                        onClick={() => setAnalyticsEnabled(true)}
                                                        className={`min-h-[36px] min-w-[48px] rounded-sm px-3 text-sm font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ekc-light-green ${analyticsEnabled ? 'bg-ekc-light-green text-custom-black' : 'text-white/70 hover:text-white'}`}
                                                    >
                                                        {copy.onLabel}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
                                            <button
                                                type="button"
                                                onClick={() => chooseAnalytics(analyticsEnabled)}
                                                className={`${buttonBase} bg-ekc-light-green text-custom-black hover:bg-white`}
                                            >
                                                {copy.saveLabel}
                                            </button>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="consent-summary"
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 12 }}
                                        transition={{ duration: 0.18 }}
                                        className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end"
                                    >
                                        <div>
                                            <p className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-ekc-light-green">{copy.summaryEyebrow}</p>
                                            <h2 id="cookie-preferences-title" className="text-xl font-bold sm:text-2xl">{copy.summaryTitle}</h2>
                                            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/75 sm:text-base">
                                                {copy.summaryBody} {copy.privacyPolicyPrefix}{' '}
                                                <Link className="font-semibold text-white underline decoration-ekc-light-green decoration-2 underline-offset-4 hover:text-ekc-light-green" to="/privacy-policy">
                                                    {copy.privacyPolicyLabel}
                                                </Link>.
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
                                            <button
                                                type="button"
                                                onClick={() => chooseAnalytics(false)}
                                                className={`${buttonBase} border border-white/30 text-white hover:border-white hover:bg-white/10`}
                                            >
                                                {copy.declineLabel}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setIsManaging(true)}
                                                className={`${buttonBase} text-white hover:bg-white/10`}
                                            >
                                                {copy.manageLabel}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => chooseAnalytics(true)}
                                                className={`${buttonBase} bg-ekc-light-green text-custom-black hover:bg-white`}
                                            >
                                                {copy.acceptLabel}
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.section>
                ) : (
                    <motion.button
                        key="cookie-preferences-trigger"
                        type="button"
                        onClick={() => {
                            setAnalyticsEnabled(preferences?.analytics ?? false);
                            setIsManaging(true);
                            setIsOpen(true);
                        }}
                        className="pointer-events-auto mb-3 ml-3 min-h-[44px] border border-white/20 bg-custom-black px-4 py-2 text-sm font-bold text-white shadow-lg transition hover:border-ekc-light-green hover:text-ekc-light-green focus:outline-none focus-visible:ring-2 focus-visible:ring-ekc-light-green sm:mb-5 sm:ml-5"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                    >
                        {copy.triggerLabel}
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CookiePreferences;
